import { createAlert, dismissAlert } from "actions/alerts";
import keyConverter from "services/key-converter";
import { AsyncAction } from "actions/types";
import { Action } from "redux";
import { setPage } from "actions/router";

export function getTrainedExpressionsAction(
  payload: {responseId: string; lastExpressionId: string; clear: boolean}
): Action {
  const { responseId, lastExpressionId, clear = true } = payload;
  let getExpressionsURL = `/responses/${responseId}/expressions/`;

  if (lastExpressionId) {
    getExpressionsURL = `/responses/${responseId}/expressions/?after=${
      lastExpressionId
    }`;
  }

  return {
    CALL_API: {
      method: "get",
      endpoint: getExpressionsURL,
      args: {
        responseId,
        clear
      },
      types: [
        "GET_TRAINED_EXPRESSIONS_REQUEST",
        "GET_TRAINED_EXPRESSIONS_SUCCESS",
        "GET_TRAINED_EXPRESSIONS_FAILURE"
      ]
    }
  } as unknown as Action;
}

export function getTrainedExpressionsCountAction(response: {responseId: string}): Action {
  const { responseId } = response;

  return {
    CALL_API: {
      method: "get",
      endpoint: `/responses/${responseId}/expressions/count`,
      args: {
        responseId
      },
      types: [
        "GET_TRAINED_EXPRESSIONS_COUNT_REQUEST",
        "GET_TRAINED_EXPRESSIONS_COUNT_SUCCESS",
        "GET_TRAINED_EXPRESSIONS_COUNT_FAILURE"
      ]
    }
  } as unknown as Action;
}

/* Assigns a responseId to an untrained expression */
function moveExpression(expressionId: string, responseId: string, body: string): Action {
  return {
    CALL_API: {
      method: "patch",
      endpoint: `/expressions/${expressionId}`,
      payload: {
        response_id: responseId,
        body
      },
      types: [
        "MOVE_EXPRESSION_REQUEST",
        "MOVE_EXPRESSION_SUCCESS",
        "MOVE_EXPRESSION_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: 400
        },
        {
          request: createAlert,
          fireOnStatus: "success",
          args: {
            message: `Your message, "${body}", was successfully moved!`,
            alertType: "success"
          }
        }
      ]
    }
  } as unknown as Action;
}

/** Dispatches an alert for when an expression already exists on another answer */
function alreadyTrainedAlert(
  args: {
    response: {data: {response_id: string; expression_id: string}};
    newResponseId: string;
    body: string;
  }
): AsyncAction {
  const existingResponseId = args.response.data.response_id;
  const existingExpressionId = args.response.data.expression_id;

  return (dispatch, getState) => {
    const existingResponse = getState().responses.find(
      el => el.id === existingResponseId
    );
    const message = `Sorry, that question is already trained to the answer "${existingResponse.handle}".`;
    const openAnswerButton = {
      label: "Open Answer",
      action: () => {
        dispatch(dismissAlert());
        dispatch(setPage(`/answers/${existingResponseId}`));
      }
    };
    const moveHereButton = {
      label: "Move here",
      action: () => {
        dispatch(dismissAlert());
        dispatch(moveExpression(
          existingExpressionId,
          args.newResponseId,
          args.body
        ));
      }
    };
    const buttons = (existingResponseId === args.newResponseId)
      ? [openAnswerButton]
      : [openAnswerButton, moveHereButton];
    const newArgs = { ...args, message, buttons };

    dispatch(createAlert(newArgs));
  };
}

/** Dispatches an alert for when an expression already exists on another answer */
function alreadyTrainedEditAlert(
  args: {response: {data: {errors: {response_id: string}[]}}}
): AsyncAction {
  // at this time, we only ever try to edit one expression at a time
  // so the correct error will always be the first (and only) in the errors array
  const existingResponseId = args.response.data.errors[0].response_id;

  return (dispatch, getState) => {
    const existingResponse = getState().responses.find(
      el => el.id === existingResponseId
    );
    const message = `Sorry, that question is already trained to the answer "${existingResponse.handle}".`;
    const openAnswerButton = {
      label: "Open Answer",
      action: () => {
        dispatch(dismissAlert());
        dispatch(setPage(`/answers/${existingResponseId}`));
      }
    };

    const newArgs = { ...args, message, buttons: [openAnswerButton] };

    dispatch(createAlert(newArgs));
  };
}

export function createExpressionAction(responseId: string, body: string): AsyncAction {
  return dispatch => dispatch({
    CALL_API: {
      method: "post",
      endpoint: "/expressions/",
      payload: {
        response_id: responseId,
        body
      },
      types: [
        "CREATE_EXPRESSION_REQUEST",
        "CREATE_EXPRESSION_SUCCESS",
        "CREATE_EXPRESSION_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: alreadyTrainedAlert,
          fireOnStatus: 400,
          args: {
            newResponseId: responseId,
            body
          }
        },
        {
          request: createAlert,
          fireOnStatus: "success",
          args: {
            message: `Your message, "${body}", was successfully trained!`,
            alertType: "success",
            buttons: [
              {
                label: "Open Answer",
                action: () => {
                  dispatch(dismissAlert());
                  dispatch(setPage(`/answers/${responseId}`));
                }
              }
            ]
          }
        }
      ]
    }
  } as unknown as Action);
}

/* For backward compatibility */
export const createExpression = createExpressionAction;

/** Calls the modify_expression rpc endpoint to update the expression body */
export function updateExpressionAction(expressionId: string, body: string): Action {
  const operations = [{
    expressionId,
    expressionData: {
      body
    }
  }];

  const payload = keyConverter({
    operationType: "batch_edit",
    operations
  }, "underscore");

  return {
    CALL_API: {
      method: "post",
      endpoint: "/expressions/rpc/modify_expressions",
      payload,
      types: [
        "PATCH_EXPRESSION_REQUEST",
        "PATCH_EXPRESSION_SUCCESS",
        "PATCH_EXPRESSION_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: 400,
          args: {
            message: "Sorry, that question could not be trained.",
            alertType: "warning"
          }
        },
        {
          request: alreadyTrainedEditAlert,
          fireOnStatus: 409
        },
        {
          request: createAlert,
          fireOnStatus: 207,
          args: {
            message: "Sorry, one (or more) of those questions could not be trained.",
            alertType: "warning"
          }
        },
        {
          request: createAlert,
          fireOnStatus: 200,
          args: {
            message: `Your message, "${body}", was successfully trained!`,
            alertType: "success"
          }
        }
      ]
    }
  } as unknown as Action;
}

export function trainExpressionsAction(ids: string[], search: string, responseId: string): Action {
  return {
    CALL_API: {
      method: "patch",
      endpoint: "/expressions/",
      payload: {
        _ids: ids,
        search,
        response_id: responseId
      },
      args: {
        ids,
        responseId,
        clear: false
      },
      types: [
        "TRAIN_EXPRESSIONS_REQUEST",
        "TRAIN_EXPRESSIONS_SUCCESS",
        "TRAIN_EXPRESSIONS_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: getTrainedExpressionsAction,
          fireOnStatus: 200
        },
        {
          request: getTrainedExpressionsCountAction,
          fireOnStatus: 200
        }
      ]
    }
  } as unknown as Action;
}

export const trainExpressions = trainExpressionsAction;
